import { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useDispatch, useSelector } from 'react-redux';
import CustomTable from '../../../../../components/CustomTable';
import CustomModal from '../../../../../components/CustomModal';
import CustomInput from '../../../../../components/CustomInput';
import { EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { Space } from 'antd';
import { toast } from 'react-toastify';
import {
    allSmsDrivers,
    smsDriversLoadingStatus,
    getSmsDrivers,
    createSmsDriver,
    updateSmsDriver,
    deleteSmsDriver,
} from '../../../../../redux/slices/settingsSlice';

export default function SmsDriversSubPage() {
    const dispatch = useDispatch();

    const smsDriversStatus = useSelector(smsDriversLoadingStatus);
    const smsDrivers = useSelector(allSmsDrivers);

    const [isCreateDriverModalVisible, setIsCreateDriverModalVisible] =
        useState(false);
    const [isEditDriverModalVisible, setIsEditDriverModalVisible] =
        useState(false);
    const [isDeleteDriverModalVisible, setIsDeleteDriverModalVisible] =
        useState(false);
    const [data, setData] = useState({
        name: '',
        login: '',
        password: '',
        service_url: '',
    });
    const [selectedDriver, setSelectedDriver] = useState({
        id: null,
        name: '',
    });
    const [errors, setErrors] = useState({});

    useEffect(() => {
        dispatch(getSmsDrivers());
    }, [dispatch]);

    const resetState = () => {
        setData({
            name: '',
            login: '',
            password: '',
            service_url: '',
        });
    };

    const toggleCreateDriverModal = () => {
        resetState();
        setErrors({});
        setIsCreateDriverModalVisible(!isCreateDriverModalVisible);
    };

    const toggleEditDriverModal = (record) => {
        if (record === undefined) {
            setIsEditDriverModalVisible(!isEditDriverModalVisible);
        } else {
            setData({
                name: record.name,
                login: record.login,
                password: record.password,
                service_url: record.service_url,
            });
            setSelectedDriver({ id: record.id, name: record.name });
            setErrors({});
            setIsEditDriverModalVisible(!isEditDriverModalVisible);
        }
    };

    const toggleDeleteDriverModal = (record) => {
        if (record === undefined) {
            setIsDeleteDriverModalVisible(!isDeleteDriverModalVisible);
        } else {
            setSelectedDriver({ id: record.id, name: record.name });
            setIsDeleteDriverModalVisible(!isDeleteDriverModalVisible);
        }
        setErrors({});
    };

    const onInputChange = (e) => {
        const value = e.target.value;
        setData({
            ...data,
            [e.target.id]: value,
        });
    };

    const onCreateDriver = async () => {
        try {
            await dispatch(createSmsDriver(data)).unwrap();
            toast.success('New SMS driver successfully added');
            toggleCreateDriverModal();
            dispatch(getSmsDrivers());
        } catch (error) {
            setErrors(error.errors);
        }
    };

    const onEditDriver = async () => {
        try {
            await dispatch(
                updateSmsDriver({ id: selectedDriver.id, body: data }),
            ).unwrap();
            toast.success('Changes saved successfully');
            toggleEditDriverModal();
            dispatch(getSmsDrivers());
        } catch (error) {
            setErrors(error.errors);
        }
    };

    const onDeleteDriver = async () => {
        try {
            await dispatch(deleteSmsDriver(selectedDriver.id)).unwrap();
            toast.success('SMS driver successfully deleted');
            toggleDeleteDriverModal();
            dispatch(getSmsDrivers());
        } catch (error) {
            setErrors(error.errors);
        }
    };

    const getFilteredDrivers = (data, filter) => {
        const filteredData = data.filter((el) =>
            el?.name.toLowerCase().includes(filter.toLowerCase()),
        );

        return filteredData;
    };

    const actionsOption = {
        title: 'Actions',
        render: (_, record) => (
            <Space size="middle">
                <EditOutlined onClick={(e) => toggleEditDriverModal(record)} />
                <DeleteOutlined
                    onClick={(e) => toggleDeleteDriverModal(record)}
                />
            </Space>
        ),
    };

    return (
        <div>
            <Helmet>
                <title>
                    Console Defentry - Settings - Notifications - SMS drivers
                </title>
            </Helmet>
            <div className="table-wrapper">
                <CustomTable
                    tableHeader={['Name', 'Actions']}
                    functionalCell={{ Actions: actionsOption }}
                    onAddButtonClick={toggleCreateDriverModal}
                    tablePannelTitle="List of drivers"
                    searchInputPlaceholder="Search by name"
                    topPannelButtonText="Create driver"
                    data={smsDrivers}
                    getFilteredData={getFilteredDrivers}
                    isLoading={smsDriversStatus === 'loading'}
                    withFilter
                />
            </div>
            <CustomModal
                title="Create driver"
                subText="Please fill the fields below"
                subTextAlign="left"
                isModalVisible={isCreateDriverModalVisible}
                onAcceptButtonClick={onCreateDriver}
                onCancelButtonClick={toggleCreateDriverModal}
                cancelButtonText="Cancel"
                acceptButtonText="Create"
            >
                <div className="form__item-wrapper">
                    <CustomInput
                        id="name"
                        placeholder="Enter name"
                        onInputChange={onInputChange}
                        inputValue={data.name}
                        labelText="Name"
                        width="100%"
                        errorText={errors?.name}
                    />
                </div>
                <div className="form__item-wrapper">
                    <CustomInput
                        id="login"
                        placeholder="Enter login"
                        onInputChange={onInputChange}
                        inputValue={data.login}
                        labelText="Login"
                        width="100%"
                        errorText={errors?.login}
                    />
                </div>
                <div className="form__item-wrapper">
                    <CustomInput
                        id="password"
                        placeholder="Enter password"
                        onInputChange={onInputChange}
                        inputValue={data.password}
                        labelText="Password"
                        width="100%"
                        errorText={errors?.password}
                    />
                </div>
                <div className="form__item-wrapper">
                    <CustomInput
                        id="service_url"
                        placeholder="Enter service URL"
                        onInputChange={onInputChange}
                        inputValue={data.service_url}
                        labelText="Service URL"
                        width="100%"
                        errorText={errors?.service_url}
                    />
                </div>
            </CustomModal>
            <CustomModal
                title="Edit driver"
                subText="Please fill the fields below"
                subTextAlign="left"
                isModalVisible={isEditDriverModalVisible}
                onAcceptButtonClick={onEditDriver}
                onCancelButtonClick={toggleEditDriverModal}
                cancelButtonText="Cancel"
                acceptButtonText="Update"
            >
                <div className="form__item-wrapper">
                    <CustomInput
                        id="name"
                        placeholder="Enter name"
                        onInputChange={onInputChange}
                        inputValue={data.name}
                        labelText="Name"
                        width="100%"
                        errorText={errors?.name}
                    />
                </div>
                <div className="form__item-wrapper">
                    <CustomInput
                        id="login"
                        placeholder="Enter login"
                        onInputChange={onInputChange}
                        inputValue={data.login}
                        labelText="Login"
                        width="100%"
                        errorText={errors?.login}
                    />
                </div>
                <div className="form__item-wrapper">
                    <CustomInput
                        id="password"
                        placeholder="Enter password"
                        onInputChange={onInputChange}
                        inputValue={data.password}
                        labelText="Password"
                        width="100%"
                        errorText={errors?.password}
                    />
                </div>
                <div className="form__item-wrapper">
                    <CustomInput
                        id="service_url"
                        placeholder="Enter service URL"
                        onInputChange={onInputChange}
                        inputValue={data.service_url}
                        labelText="Service URL"
                        width="100%"
                        errorText={errors?.service_url}
                    />
                </div>
            </CustomModal>
            <CustomModal
                title={
                    <p>
                        Are you sure you want to delete
                        <span className="highlighted-text">{` ${selectedDriver.name} `}</span>
                        driver?
                    </p>
                }
                subText="You will delete this driver without recovery"
                subTextAlign="center"
                isModalVisible={isDeleteDriverModalVisible}
                onAcceptButtonClick={onDeleteDriver}
                onCancelButtonClick={toggleDeleteDriverModal}
                cancelButtonText="Cancel"
                acceptButtonText="Delete"
            >
                {errors.sms_driver_id && (
                    <p className="modal__error-text">
                        {errors.sms_driver_id[0]}
                    </p>
                )}
            </CustomModal>
        </div>
    );
}
